import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords } from '../services/PersonasService';
import { deleteRecord as deleteAlias } from '../../aliases/services/AliasesService';
import { useAppSelector } from '../../../hooks/useTypedReactRedux';
import { selectionSelector } from '../selectors';
import { PersonaStatus } from '../types';

export const usePersonas = (externalStatusFilter?: PersonaStatus | null) => {
    const queryClient = useQueryClient();
    const selection = useAppSelector((state) => state.personas ? selectionSelector(state) : {});

    const activeStatusFilter = externalStatusFilter !== undefined ? externalStatusFilter : null;

    const personasQuery = useInfiniteQuery({
        queryKey: ['personas', activeStatusFilter],
        queryFn: ({ pageParam }: { pageParam: string | null }) =>
            fetchRecords({ limit: 10, nextToken: pageParam, statusFilter: activeStatusFilter }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: { id: string, email: string, givenName: string, familyName: string }[]) => {
            for (const item of ids) {
                const aliasName = [item.givenName, item.familyName].join('.');
                await deleteAlias(item.id, aliasName);
                await deleteRecord(item.id, item.email);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['personas'] });
            queryClient.invalidateQueries({ queryKey: ['aliases'] });
        }
    });

    const fetchRecordsAction = () => {
        if (personasQuery.hasNextPage) {
            personasQuery.fetchNextPage();
        }
    };

    const refetchRecords = () => {
        queryClient.invalidateQueries({ queryKey: ['personas'] });
    };

    const deleteRecords = () => {
        const selectedIds = Object.keys(selection).map(key => ({
            id: selection[key].id,
            email: selection[key].email,
            givenName: selection[key].givenName,
            familyName: selection[key].familyName
        }));
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const allItems = personasQuery.data?.pages?.flatMap(page => 
        (page && 'items' in page) ? page.items : []
    ) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        visibleCount: allItems.length,
        loading: personasQuery.isLoading,
        nextToken: personasQuery.hasNextPage ? 'has-more' : null,
        isFetchingNextPage: personasQuery.isFetchingNextPage,
        fetchRecords: fetchRecordsAction,
        refetchRecords,
        deleteRecords,
    };
};
